import React, { useEffect, useState } from "react";
import styles from "./GeneralInformation.module.scss";
import LeaderProfileImage from "../../../assets/images/LightTheme/leaderProfileImage.svg";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { useAppSelector } from "../../../redux/hooks";
import Icon from "../../common/Icon/Icon";
import ImagePreview from "../../common/ImagePreview/ImagePreview";
import Dropdown from "../../common/Dropdown/Dropdown";
import ErrorMessage from "../../../utils/fieldErrorMesssage";
import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal/Modal";
import UploadImage from "../../common/UploadImage/UploadImage";
import { disableEnableScroll } from "../../../utils/disableEnableScroll";
import {
  useGetGeneralInformationQuery,
  useCreateGeneralInformationMutation,
  useGetOptionMasterTimeZoneQuery,
} from "../../../redux/services/generalInformation";
import { getLanguageName, useProfileParams } from "../../../utils/common";
import { formatDateShort } from "../../../utils/common";
import ErrorInPageScreen from "../../common/ErrorInPageScreen/ErrorInPageScreen";
import ABOBackgroundLoader from "../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";
import validateEmail from "../../../utils/emailValidator";

const proficiencyOptions = [
  { code: 1, value: "profile_proficiency.1" },
  { code: 2, value: "profile_proficiency.2" },
  { code: 3, value: "profile_proficiency.3" },
];

const GeneralInformation = () => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [isEditing, setIsEditing] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [email, setEmail] = useState("");
  const [showImageUpload, setShowImageUpload] = useState(false);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [preferredName, setPreferredName] = useState("");
  const [timeZone, setTimeZone] = useState<DropdownItem | undefined>(undefined);
  const [isAPIError, setIsAPIError] = useState(false);

  const [additionalLanguages, setAdditionalLanguages] = useState<
    { language?: DropdownItem; proficiency?: DropdownItem }[]
  >([{ language: undefined, proficiency: undefined }]);

  const handleSelectLanguage = (index: number, item: DropdownItem) => {
    const updated = [...additionalLanguages];
    updated[index].language = item;
    setAdditionalLanguages(updated);
  };
  const handleSelectProficiency = (index: number, item: DropdownItem) => {
    const updated = [...additionalLanguages];
    updated[index].proficiency = item;
    setAdditionalLanguages(updated);
  };
  const handleReset = () => {
    setAdditionalLanguages([{ language: undefined, proficiency: undefined }]);
  };
  const handleSelectTimeZone = (item: DropdownItem) => {
    setTimeZone(item);
  };

  const handleAddAnother = () => {
    setAdditionalLanguages([
      ...additionalLanguages,
      { language: undefined, proficiency: undefined },
    ]);
  };
  const handleSave = async () => {
    const trimmedEmail = (email || "").trim();
    // Validate only when the field is non-empty; allow empty to pass
    if (trimmedEmail.length > 0) {
      const { isValid } = validateEmail(trimmedEmail, { required: false });
      if (!isValid) {
        setEmailError("please_enter_valid_email_address");
        return;
      }
    }
    setEmailError("");

    try {
      const formattedLanguages = additionalLanguages
        .filter((lang) => lang.language && lang.proficiency)
        .map((lang) => ({
          language: lang.language!.code,
          proficiency: lang.proficiency!.code,
        }));

      const payload: createGeneralInformation = {
        profilePhoto,
        preferredName,
        timeZoneType: timeZone?.code,
        additionalLanguage: JSON.stringify(formattedLanguages),
        overrideEmailAddress: email,
      };

      const result = await updateGeneralInformation({
        affiliateCode,
        aboNumber,
        payload,
      }).unwrap();

      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleCancel = () => {
    setEmail(resolvePrimaryEmail(userProfile));
    setIsEditing(false);
    setProfilePhoto(apiProfilePhoto);
    setShowImageUpload(false);
  };

  const [profilePhoto, setProfilePhoto] = useState<string>("");
  const [apiProfilePhoto, setApiProfilePhoto] = useState<string>("");

  useEffect(() => {
    setApiProfilePhoto(userProfile?.profilePhoto || "");
    setProfilePhoto(userProfile?.profilePhoto || "");

    try {
      const parsedLanguages: ParsedLanguage[] = JSON.parse(
        userProfile?.additionalLanguage || "[]"
      );

      const mappedLanguages = parsedLanguages?.map((lang) => ({
        language: languageOptions.find(
          (l: LanguageOption) => l.code === lang.language
        ),
        proficiency: proficiencyOptions.find(
          (p) => p.code === lang.proficiency
        ),
      }));

      setAdditionalLanguages(
        mappedLanguages.length > 0
          ? mappedLanguages
          : [{ language: undefined, proficiency: undefined }]
      );
    } catch (error) {
      console.error("Failed to parse additional languages:", error);
      setAdditionalLanguages([{ language: undefined, proficiency: undefined }]);
    }
  }, [userProfile]);

  useEffect(() => {
    disableEnableScroll(showImageUpload);
    return () => disableEnableScroll(false); // cleanup na wszelki wypadek
  }, [showImageUpload]);

  const handleConfirmUpload = (dataUrl: string | null) => {
    if (dataUrl) {
      setProfilePhoto(String(dataUrl));
    }
    setShowImageUpload(false);
  };

  const { data: timeZoneList, isLoading: isTimeZoneListLoading } =
    useGetOptionMasterTimeZoneQuery({
      filterTypeName: "profile-timezone",
    });

  const { data: languageList, isLoading: iLanguageListLoading } =
    useGetOptionMasterTimeZoneQuery({
      filterTypeName: "profile-language",
    });

  const timeZoneOptions =
    timeZoneList?.body?.options.map((timezone: any) => ({
      code: timezone?.code,
      value: `${timezone?.code} (${timezone?.value})`,
    })) ?? [];

  const languageOptions =
    languageList?.body?.options.map((lang: LanguageOption) => ({
      code: lang?.code,
      value: `profile_language.${lang?.code}`,
    })) ?? [];

  const {
    data: generalInfoData,
    isLoading: isGeneralInfoLoading,
    isError: isGeneralInfoError,
  } = useGetGeneralInformationQuery({
    affiliateCode,
    aboNumber,
  });
  const [updateGeneralInformation, { isLoading: isUpdatingInfo }] =
    useCreateGeneralInformationMutation();

  const handleAPIError = () => {
    setIsAPIError(true);
    setUserProfile(null);
  };

  const resolvePrimaryEmail = (profile?: any): string => {
    const overrideVal = profile?.overrideEmailAddress;
    if (overrideVal === null) {
      return profile?.primaryApplicantEmailAddress ?? "";
    }
    return overrideVal ?? "";
  };

  useEffect(() => {
    isGeneralInfoError && handleAPIError();
  }, [isGeneralInfoError]);

  useEffect(() => {
    if (generalInfoData) {
      const userData = generalInfoData?.body;
      setUserProfile(userData);

      setEmail(resolvePrimaryEmail(userData));
      setPreferredName(userData?.preferredName);

      const selectedTimeZone = timeZoneOptions?.find(
        (timeZone: any) => timeZone?.code === userData?.timezoneType
      );
      setTimeZone(selectedTimeZone || null);
    }
  }, [generalInfoData]);

  useEffect(() => {
    setEmailError("");
  }, [isEditing]);

  const filterDropdownElements = (index: number | null = null) => {
    let tempLangList = [...languageOptions];
    switch (index) {
      case 0:
        return tempLangList.filter(
          (obj) =>
            obj.code !== additionalLanguages?.[1]?.language?.code &&
            obj.code !== additionalLanguages?.[2]?.language?.code
        );
      case 1:
        return tempLangList.filter(
          (obj) =>
            obj.code !== additionalLanguages?.[0]?.language?.code &&
            obj.code !== additionalLanguages?.[2]?.language?.code
        );
      case 2:
        return tempLangList.filter(
          (obj) =>
            obj.code !== additionalLanguages?.[0]?.language?.code &&
            obj.code !== additionalLanguages?.[1]?.language?.code
        );
      default:
        return tempLangList;
    }
  };

  if (isGeneralInfoLoading || isUpdatingInfo) {
    return <ABOBackgroundLoader num={2} heading={t("general_information")} />;
  }

  return (
    <div className={styles.general_outer_wrapper}>
      <div className={styles.title_wrapper}>
        <div className={styles.title_inner}>
          <div className={styles.progress_bar}>
            <CircularProgressbarWithChildren
              value={userProfile?.completionRate || 0}
              strokeWidth={20}
              className={styles["circular_progress_gradient"]}
              styles={buildStyles({
                trailColor: currentTheme === "light" ? "#D9D9D9" : "#282828",
              })}
            />
          </div>
          <div className={styles.general_info_title}>
            {t("general_information")}
          </div>
        </div>
        <div className={styles.edit_icon_wrapper}>
          {isEditing ? (
            <>
              <span className={styles.cancel_text} onClick={handleCancel}>
                {t("btnCancel")}
              </span>
              <span className={styles.save_text} onClick={handleSave}>
                {t("btnSave")}
              </span>
            </>
          ) : (
            <div
              className={styles.edit_wrapper}
              onClick={() => setIsEditing(true)}
            >
              <Icon
                name="edit_goal_icon"
                size={14}
                color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                className={styles.search_icon}
              />
              <span className={styles.edit_text}>{t("edit")}</span>
            </div>
          )}
        </div>
      </div>
      {isAPIError ? (
        <ErrorInPageScreen
          handleClickTryAgain={() => {
            setIsAPIError(false);
          }}
        />
      ) : (
        <div className={styles.general_inner_wrapper}>
          <div className={styles.image_wrapper}>
            <ImagePreview
              src={profilePhoto || LeaderProfileImage}
              className={`${styles.image} ${isEditing ? styles.editable : ""}`}
              onClick={() => {
                if (isEditing) setShowImageUpload(true);
              }}
              role={isEditing ? "button" : undefined}
              tabIndex={isEditing ? 0 : -1}
            />
          </div>
          <div className={styles.first_column_wrapper}>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("name")}:</span>
              <span className={styles.description}>
                {userProfile?.primaryApplicantName || "-"}
              </span>
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("preferred_name")}:</span>
              {isEditing ? (
                <input
                  type="text"
                  value={preferredName}
                  className={styles.input}
                  placeholder={t("prefered_name_placeholder")}
                  onChange={(e) => setPreferredName(e.target.value)}
                />
              ) : (
                <span className={styles.description}>
                  {userProfile?.preferredName || "-"}
                </span>
              )}
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("primary_time_zone")}:</span>

              {isEditing ? (
                <Dropdown
                  dropdownName={t("select_time_zone")}
                  items={timeZoneOptions}
                  selectedItem={timeZone}
                  handleSelectItemCallback={handleSelectTimeZone}
                  size="md"
                  addScroll={true}
                />
              ) : (
                <span className={styles.description}>
                  {timeZone?.value || "-"}
                </span>
              )}
            </div>
          </div>

          <div className={styles.second_column_wrapper}>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("entry_date")}:</span>
              <span className={styles.description}>
                {formatDateShort(userProfile?.entryDate || "")}
              </span>
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("primary_language")}</span>
              <span className={styles.description}>
                {getLanguageName(userProfile?.primaryApplicantLanguage || "-")}
              </span>
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("additional_languages")}:</span>
              {isEditing ? (
                <div className={styles.langugae_wrapper}>
                  {additionalLanguages.map((lang, index) => (
                    <div>
                      <div className={styles.dropdown_wrapper}>
                        <Dropdown
                          dropdownName={t("filter_categories_title.language")}
                          items={filterDropdownElements(index)}
                          selectedItem={lang.language}
                          handleSelectItemCallback={(item) =>
                            handleSelectLanguage(index, item)
                          }
                          className={styles.leader_dropdown}
                          size="md"
                          addScroll={true}
                        />

                        <Dropdown
                          dropdownName={t("proficiency")}
                          items={proficiencyOptions}
                          selectedItem={lang.proficiency}
                          handleSelectItemCallback={(item) =>
                            handleSelectProficiency(index, item)
                          }
                          addScroll
                          size="md"
                        />
                      </div>
                    </div>
                  ))}
                  {
                    <div className={`${styles.add_reset_wrapper}`}>
                      {additionalLanguages.length < 3 && (
                        <span
                          className={`${styles.add_another} ${
                            !additionalLanguages.some((lang) => lang.language)
                              ? styles.disabled
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              additionalLanguages.some((lang) => lang.language)
                            ) {
                              handleAddAnother();
                            }
                          }}
                        >
                          <Icon
                            name={"plus_icon"}
                            size={14}
                            className={styles.tag_icon}
                            color={
                              currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"
                            }
                          />
                          <span className={styles.add_another_text}>
                            {t("add_another")}
                          </span>
                        </span>
                      )}
                      {additionalLanguages.some((lang) => lang.language) && (
                        <span
                          className={styles.reset_wrapper}
                          onClick={handleReset}
                        >
                          <Icon
                            name={"reset_icon"}
                            size={14}
                            className={styles.tag_icon}
                            color={
                              currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"
                            }
                          />
                          <span className={styles.reset_text}>
                            {t("reset")}
                          </span>
                        </span>
                      )}
                    </div>
                  }
                </div>
              ) : (
                <span className={styles.description}>
                  {additionalLanguages.map((lang, index) => (
                    <div key={index}>
                      {lang.language?.value && `${t(lang.language?.value)},`}{" "}
                      {lang.proficiency?.value
                        ? t(lang.proficiency?.value)
                        : ""}
                    </div>
                  ))}
                </span>
              )}
            </div>
          </div>

          <div className={styles.third_column_wrapper}>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("primary_email")}:</span>

              {isEditing ? (
                <div className={styles.input_wrapper}>
                  <input
                    type="text"
                    className={`${styles.input} ${emailError ? styles.invalid : ""}`}
                    value={email}
                    placeholder="Email Address"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <ErrorMessage
                    message={emailError ? t(emailError) : ""}
                    className={styles.error_message}
                  />
                </div>
              ) : (
                <span className={styles.description}>
                  {resolvePrimaryEmail(userProfile) || "-"}
                </span>
              )}
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("business_affiliation")}:</span>
              <span className={styles.description}>
                {userProfile?.businessAffiliate}
              </span>
            </div>
            <div className={styles.general_info}>
              <span className={styles.title}>{t("birthday")}:</span>
              <span className={styles.description}>
                {userProfile?.primaryApplicantBirthday
                  ? formatDateShort(userProfile.primaryApplicantBirthday)
                  : "-"}{" "}
                {t("primary_applicant")}
              </span>
              {userProfile?.secondaryApplicantBirthday && (
                <span className={styles.description}>
                  {formatDateShort(userProfile.secondaryApplicantBirthday)}{" "}
                  {t("secondary_applicant")}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        customWidthClass={styles.modalCustom}
        showDrawer={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        customStyle={{
          display: "flex",
          width: 472,
          padding: "20px 16px 24px 16px",
          flexDirection: "column",
          alignItems: "flex-end",
          borderRadius: "20px",
          background: "var(--surface-surface-modal, #FFF)",
        }}
      >
        <UploadImage onConfirm={handleConfirmUpload} />
      </Modal>
    </div>
  );
};

export default GeneralInformation;
